import { TFunction } from 'i18next'
import { ROUTES } from '../../constants'
import { HeaderPopoverItem } from './HeaderPopover'

export interface NavItem {
  key: string
  label: string
  to?: string
  external?: boolean
  /** When set, the item runs an action instead of navigating. */
  action?: 'referFriend'
  /** When set, the item becomes an expandable group revealing these sub-items. */
  children?: NavItem[]
  collapsible?: boolean
}

const ts = (t: TFunction, key: string) => String(t(key))

/** Shop sub-categories surfaced under the Shop accordion on mobile. */
export const getShopCategoryItems = (t: TFunction): NavItem[] => [
  {
    key: 'fashion',
    label: ts(t, 'shared.fashion'),
    to: ROUTES.fashionCategory,
  },
  { key: 'home', label: ts(t, 'shared.home'), to: ROUTES.homeCategory },
  { key: 'beauty', label: ts(t, 'shared.beauty'), to: ROUTES.beautyCategory },
  {
    key: 'lifestyle',
    label: ts(t, 'shared.lifestyle'),
    to: ROUTES.lifestyleCategory,
  },
]

/** Logged-out desktop: Join, Brands, Community */
export const getLoggedOutDesktopLinks = (t: TFunction): NavItem[] => [
  { key: 'join', label: ts(t, 'shared.join'), to: ROUTES.join },
]

/** Logged-out desktop hamburger */
export const getLoggedOutMenuExtras = (t: TFunction): HeaderPopoverItem[] => [
  {
    key: 'howItWorks',
    label: ts(t, 'header.howItWorks'),
    to: ROUTES.howItWorks,
  },
  { key: 'about', label: ts(t, 'header.about'), to: ROUTES.about },
]

/** Logged-in: Tools dropdown */
export const getLoggedInToolsItems = (
  t: TFunction,
  shoppingBoardLink: string
): HeaderPopoverItem[] => [
  {
    key: 'createShareLink',
    label: ts(t, 'createLink.createShareLink'),
    to: ROUTES.createShareLink,
  },
  {
    key: 'Board',
    label: ts(t, 'header.yourShoppingBoard'),
    to: shoppingBoardLink,
  },
]

/** Logged-in desktop: items shown in the avatar's account dropdown */
export const getLoggedInMenuExtras = (t: TFunction): HeaderPopoverItem[] => [
  {
    key: 'accountSettings',
    label: ts(t, 'header.accountSettings'),
    to: ROUTES.settings,
  },
  {
    key: 'browserExtension',
    label: ts(t, 'company.browserExtension.title'),
    to: ROUTES.browserExtension,
  },
  {
    key: 'howItWorks',
    label: ts(t, 'header.howItWorks'),
    to: ROUTES.howItWorks,
  },
  {
    key: 'referFriend',
    label: ts(t, 'header.referFriend'),
    action: 'referFriend',
  },
  { key: 'signOut', label: ts(t, 'header.signOut'), action: 'signOut' },
]

/** Mobile logged-out: primary links + secondary */
export const getLoggedOutMobileNav = (t: TFunction): NavItem[] => [
  {
    key: 'howItWorks',
    label: ts(t, 'header.howItWorks'),
    to: ROUTES.howItWorks,
  },
  { key: 'brands', label: ts(t, 'shared.brands'), to: ROUTES.brands },
  {
    key: 'shop',
    label: ts(t, 'shared.shop'),
    children: getShopCategoryItems(t),
  },
  {
    key: 'community',
    label: ts(t, 'header.community'),
    to: ROUTES.theChirpyest,
  },
  {
    key: 'blog',
    label: ts(t, 'header.blog'),
    to: ROUTES.blog,
    external: true,
  },
  {
    key: 'curaeter',
    label: ts(t, 'header.curaeter'),
    to: ROUTES.curaeter,
  },
  {
    key: 'browserExtension',
    label: ts(t, 'company.browserExtension.title'),
    to: ROUTES.browserExtension,
  },
]

/** Mobile logged-in */
export const getLoggedInMobileNav = (
  t: TFunction,
  shoppingBoardLink: string
): NavItem[] => [
  { key: 'brands', label: ts(t, 'shared.brands'), to: ROUTES.brands },
  {
    key: 'shop',
    label: ts(t, 'shared.shop'),
    children: getShopCategoryItems(t),
  },
  {
    key: 'share',
    label: ts(t, 'shared.share'),
    collapsible: false,
    children: [
      {
        key: 'shoppingBoard',
        label: ts(t, 'home.shoppingBoard'),
        to: shoppingBoardLink,
      },
      {
        key: 'createShareLink',
        label: ts(t, 'shared.shareLink'),
        to: ROUTES.createShareLink,
      },
      {
        key: 'community',
        label: ts(t, 'shared.theChirpyest'),
        to: ROUTES.theChirpyest,
      },
    ],
  },
  {
    key: 'earn',
    label: ts(t, 'shared.earn'),
    collapsible: false,
    children: [
      {
        key: 'yourAccount',
        label: ts(t, 'header.yourAccount'),
        to: ROUTES.yourAccount,
      },
      {
        key: 'referFriend',
        label: ts(t, 'shared.referFriendAndEarn'),
        action: 'referFriend',
      },
    ],
  },
  {
    key: 'blog',
    label: ts(t, 'header.blog'),
    to: ROUTES.blog,
    external: true,
  },
  {
    key: 'curaeter',
    label: ts(t, 'header.curaeter'),
    to: ROUTES.curaeter,
  },
  {
    key: 'howItWorks',
    label: ts(t, 'header.howItWorks'),
    to: ROUTES.howItWorks,
  },
  {
    key: 'browserExtension',
    label: ts(t, 'company.browserExtension.title'),
    to: ROUTES.browserExtension,
  },
]
